import { useEffect, useState, useCallback } from "react";
import { MdClose } from "react-icons/md";
import RolesForm from "../Components/RolesForm";
import RolesTable from "../Components/RolesTable";
import RolesDetail from "../Components/RolesDetail";
import "../styles/mainpage.css";

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showRoleDetail, setShowRoleDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles`,
        {
          headers: { Authorization: `Bearer ${token}` }
        });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setRoles(data);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleAddRole = async (role) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(role),
      });
      if (res.ok) {
        closeAddModal();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.message || data.error || "Could not save the user.");
      }
    } catch (error) {
      console.error("Failed to add role:", error);
    }
  };

  const handleUpdateRole = async (role) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/roles/${role._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json", Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(role),
        }
      );
      if (res.ok) {
        closeAddModal();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.message || data.error || "Could not save the user.");
      }
    } catch (error) {
      console.error("Failed to update role:", error);
    }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        closeDetailModal();
      } else {
        alert("Could not delete the user.");
      }
    } catch (error) {
      console.error("Failed to delete role:", error);
    }
  };

  const closeAddModal = () => {
    setIsAdding(false);
    fetchRoles();
  };

  const closeDetailModal = () => {
    setShowRoleDetail(false);
    fetchRoles();
  };

  const handleEditClick = () => {
    if (selectedRole) {
      setIsAdding(true);
      setShowRoleDetail(false);
    }
  };

  return (
    <div className="management-page">
      <h1>User Management </h1>
      <button
        className="record-button"
        onClick={() => {
          setIsAdding(true);
          setSelectedRole(null);
        }}
      >
        Add User
      </button>

      {isAdding && (
        <div className="modal-overlay">
          <div className="modal-container">
            <button className="close-button" onClick={closeAddModal}>
              <MdClose size={24} />
            </button>
            <RolesForm
              onSubmit={selectedRole ? handleUpdateRole : handleAddRole}
              initialData={selectedRole}
            />
          </div>
        </div>
      )}

      {showRoleDetail && selectedRole && (
        <div className="detail-modal-overlay">
          <div className="detail-modal-content">
            <button className="detail-close-button" onClick={closeDetailModal}>
              <MdClose size={24} />
            </button>
            <RolesDetail
              role={selectedRole}
              onEdit={handleEditClick}
              onDelete={() => handleDeleteRole(selectedRole._id)}
            />
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        !isAdding &&
        !showRoleDetail && (
          <RolesTable
            roles={roles}
            onCardClick={(id) => {
              const role = roles.find((rol) => rol._id === id);
              setSelectedRole(role);
              setShowRoleDetail(true);
            }}
          />
        )
      )}
    </div>
  );
};

export default RolesManagement;
